import { useQueries } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Fetch multiple league details with user ranks
 * Uses useQueries to fetch all leagues in parallel
 */
export const useMultipleLeaguesWithRank = (leagueIds, userId) => {
  const { getAccessTokenSilently } = useAuth0();

  const queries = useQueries(
    leagueIds.map((leagueId) => ({
      queryKey: ["leagueDetailsWithRank", leagueId, userId],
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL;

        // Fetch league details
        const { data: leagueData } = await axios.get(
          `${apiUrl}/api/league/${leagueId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Cheap rank lookup — the backend computes the user's position with a
        // count query instead of us pulling a page of members to find it.
        let userRank = null;
        try {
          const { data: rankData } = await axios.get(
            `${apiUrl}/api/league/${leagueId}/my-rank`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          userRank = rankData.rank;
        } catch (error) {
          console.error(`Error fetching rank for league ${leagueId}:`, error);
        }

        return {
          ...leagueData,
          _id: leagueId,
          name: leagueData.leagueName,
          userRank: userRank,
          memberCount: leagueData.members?.length || 0,
          openSeats: leagueData.numPlayers - (leagueData.members?.length || 0),
          totalSeats: leagueData.numPlayers,
          status: leagueData.status,
        };
      },
      enabled: !!leagueId && !!userId,
      staleTime: 60000, // 1 minute
    })),
  );

  // Combine results
  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const data = queries.map((query) => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
  };
};
